import { 
  Firestore, 
  doc, 
  getDoc,
  updateDoc,
  serverTimestamp 
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { getCampLocationsFromOrg } from "./getCampLocationsFromOrg";
import { getLatestLocationVersion } from "./locationVersion/versionStatusService";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";

export interface CopyVariationsResult {
  success: boolean;
  copiedCount: number;
  errors: string[];
  operationId: string;
}

export interface LocationOption {
  id: string;
  name: string;
  variationsCount: number;
}

/**
 * Validates that the user has admin privileges
 */
const validateAdminAccess = (userRole?: string): void => {
  if (userRole !== 'admin_user') {
    throw new Error('Copy variations operations are only available to admin users');
  }
};

/**
 * Gets all locations in the same organization as source options
 */
export const getLocationOptionsForOrg = async (
  db: Firestore, 
  orgId: string, 
  excludeLocationId: string
): Promise<LocationOption[]> => {
  try {
    console.log(`[copyVariationsService] Getting location options for org ${orgId}, excluding ${excludeLocationId}`);
    
    const allLocations = await getCampLocationsFromOrg(db, orgId);
    
    // Filter out the current location and map to options
    const locationOptions = allLocations
      .filter(location => location.id !== excludeLocationId)
      .map(location => ({
        id: location.id,
        name: location.name || 'Unnamed Location',
        variationsCount: Array.isArray(location.variations) ? location.variations.length : 0
      }))
      // Sort by name for better UX
      .sort((a, b) => a.name.localeCompare(b.name));
    
    console.log(`[copyVariationsService] Found ${locationOptions.length} location options`);
    return locationOptions;
  } catch (error) {
    console.error('[copyVariationsService] Error getting location options:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to fetch location options: ${errorMessage}`);
  }
};

/**
 * Copies variations from a published source location to the current location's latest draft
 */
export const copyVariationsFromLocation = async (
  db: Firestore,
  sourceLocationId: string,
  destinationLocationId: string,
  userId: string,
  userRole?: string
): Promise<CopyVariationsResult> => {
  const operationId = `copy_variations_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  try {
    console.log(`[copyVariationsService] Starting copy variations operation ${operationId}`);
    console.log(`[copyVariationsService] From: ${sourceLocationId}, To: ${destinationLocationId}, User: ${userId}, Role: ${userRole}`);
    
    // Validate admin access
    validateAdminAccess(userRole);
    
    // Get the source location's published data
    const sourceDocRef = doc(db, "campLocations", sourceLocationId);
    const sourceDoc = await getDoc(sourceDocRef);
    
    if (!sourceDoc.exists()) {
      throw new Error('Source location not found');
    }
    
    const sourceData = sourceDoc.data() as CampLocation;
    const sourceVariations = sourceData.variations || [];
    
    if (sourceVariations.length === 0) {
      console.log(`[copyVariationsService] Source location has no variations to copy`);
      return {
        success: true,
        copiedCount: 0,
        errors: [],
        operationId
      };
    }
    
    console.log(`[copyVariationsService] Found ${sourceVariations.length} variations to copy`);

    // KB-3308: live-only path writes the destination's live campLocations doc directly (no draft
    // version required, no snapshot). For a published destination the copied variations become
    // public immediately — the deliberate consequence of live-only editing.
    if (isLocationEditLiveSource()) {
      const destRef = doc(db, "campLocations", destinationLocationId);
      const destDoc = await getDoc(destRef);
      if (!destDoc.exists()) {
        throw new Error('Destination location not found');
      }
      const existingVariations = (destDoc.data() as CampLocation).variations || [];
      const updatedVariations = [...existingVariations, ...sourceVariations];
      await updateDoc(destRef, {
        variations: updatedVariations,
        // Editor stamp rides in the same write (KB-3471 ruling 1).
        lastEditedBy: doc(db, "users", userId),
        lastEditedAt: serverTimestamp(),
      });
      console.log(`[copyVariationsService] (live-only) wrote ${updatedVariations.length} variations to the live doc`);
      return {
        success: true,
        copiedCount: sourceVariations.length,
        errors: [],
        operationId,
      };
    }

    // Get the destination location's latest draft version
    const destinationVersion = await getLatestLocationVersion(db, destinationLocationId);
    if (!destinationVersion) {
      throw new Error('Destination location has no draft version');
    }
    
    // Prepare the updated variations array
    const existingVariations = destinationVersion.data.variations || [];
    const updatedVariations = [...existingVariations, ...sourceVariations];
    
    console.log(`[copyVariationsService] Merging ${sourceVariations.length} new variations with ${existingVariations.length} existing variations`);
    
    // Update the destination version document
    const versionRef = doc(db, "campLocationVersions", destinationVersion.versionId);
    await updateDoc(versionRef, {
      "data.variations": updatedVariations,
      changeDescription: "Copy variations from another location",
      action: "update",
      timestamp: serverTimestamp(),
      user: doc(db, "users", userId)
    });
    
    console.log(`[copyVariationsService] Successfully updated destination with ${updatedVariations.length} total variations`);
    
    // Log the operation for audit trail
    console.log(`[copyVariationsService] Operation ${operationId} completed:`, {
      sourceLocationId,
      destinationLocationId,
      userId,
      copiedCount: sourceVariations.length,
      totalVariationsAfter: updatedVariations.length,
      timestamp: new Date().toISOString()
    });
    
    return {
      success: true,
      copiedCount: sourceVariations.length,
      errors: [],
      operationId
    };
    
  } catch (error) {
    console.error(`[copyVariationsService] Copy variations operation ${operationId} failed:`, error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Copy variations operation failed: ${errorMessage}`);
  }
}; 